import { useState, useEffect } from 'react';
import { X, Lock, Unlock, Eye, EyeOff } from 'lucide-react';
import { supabase, waitForSupabaseAuthReady } from '../lib/supabase';
import { auth } from '../lib/auth';

interface CreateRoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateSuccess?: (roomId: string) => void;
}

export default function CreateRoomModal({ isOpen, onClose, onCreateSuccess }: CreateRoomModalProps) {
  const [roomName, setRoomName] = useState('');
  const [roomPassword, setRoomPassword] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 当密码变化时自动切换公开/私密状态
  useEffect(() => {
    if (roomPassword.length > 0 && !isPrivate) {
      setIsPrivate(true);
    }
  }, [roomPassword]);

  const handleTogglePrivate = (privateValue: boolean) => {
    if (privateValue && roomPassword.length === 0) {
      setError('私密房间必须设置 6 位密码');
      return;
    }
    setIsPrivate(privateValue);
    if (!privateValue) {
      setRoomPassword('');
      setError('');
    }
  };

  if (!isOpen) return null;

  const validateForm = (): boolean => {
    if (!roomName.trim()) {
      setError('房间名称不能为空');
      return false;
    }
    if (roomName.trim().length < 1) {
      setError('房间名称至少需要 1 个字符');
      return false;
    }
    if (isPrivate && roomPassword.length !== 6) {
      setError('房间密码必须是 6 位数字');
      return false;
    }
    return true;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      await waitForSupabaseAuthReady();
      
      const user = auth.getCurrentUser();
      if (!user) {
        setError('请先登录');
        setLoading(false);
        return;
      }

      // 根据昵称获取或创建玩家资料
      const { data: playerProfile, error: profileError } = await supabase.rpc(
        'get_or_create_player_profile',
        { p_nickname: user.nickname }
      );

      if (profileError || !playerProfile) {
        console.error('[CreateRoomModal] 获取玩家资料失败:', profileError);
        setError('获取玩家资料失败，请重试');
        setLoading(false);
        return;
      }

      const playerId = playerProfile as number;

      // 获取当前最大房间 ID
      const { data: allRooms } = await supabase
        .from('online_game_rooms_cce0d86f_8f5')
        .select('id')
        .eq('is_deleted', 'n')
        .order('id', { ascending: false })
        .limit(1);

      const nextId = (allRooms && allRooms.length > 0 ? allRooms[0].id : 0) + 1;

      // 检查房间名是否重复
      const { data: existingRooms } = await supabase
        .from('online_game_rooms_cce0d86f_8f5')
        .select('room_name')
        .eq('room_name', roomName.trim())
        .eq('is_deleted', 'n');

      if (existingRooms && existingRooms.length > 0) {
        setError('房间名称已存在，请使用其他名称');
        setLoading(false);
        return;
      }

      // 调用 host_create_room RPC
      const { data, error: createError } = await supabase.rpc('host_create_room', {
        p_room_name: roomName.trim(),
        p_game_mode: 'standard',
        p_max_players: 10,
        p_min_players: 5,
        p_is_private: isPrivate,
        p_room_password: isPrivate ? roomPassword : null,
        p_host_player_id: playerId,
        p_host_nickname: user.nickname
      });

      if (createError) {
        console.error('[CreateRoomModal] 创建房间失败:', createError);
        setError('创建房间失败，请稍后重试');
        setLoading(false);
        return;
      }

      const result = data as any;
      if (!result.success) {
        setError(result.error || '创建房间失败');
        setLoading(false);
        return;
      }

      if (result.data) {
        onCreateSuccess?.(result.data.room_id.toString());
        handleClose();
      }
    } catch (err) {
      console.error('[CreateRoomModal] 创建房间异常:', err);
      setError('创建房间失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRoomName('');
    setRoomPassword('');
    setIsPrivate(false);
    setShowPassword(false);
    setError('');
    onClose();
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setRoomPassword(value);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={handleClose}
    >
      <div
        className="bg-avalon-card rounded-xl p-5 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-white font-semibold text-lg">创建房间</h3>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg bg-avalon-bg-dark/50 flex items-center justify-center text-avalon-text hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          {/* 房间名称 */}
          <div>
            <label className="block text-sm text-avalon-text mb-2">
              房间名称 <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              placeholder="请输入房间名称"
              maxLength={20}
              className="w-full px-4 py-2.5 rounded-lg bg-avalon-bg-dark border border-avalon-purple/30 text-white placeholder-avalon-text focus:outline-none focus:border-avalon-gold transition-colors"
            />
          </div>

          {/* 房间密码 */}
          <div>
            <label className="block text-sm text-avalon-text mb-2">
              房间密码 <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={roomPassword}
                onChange={handlePasswordChange}
                placeholder="输入 6 位密码"
                className="w-full px-4 py-2.5 pr-12 rounded-lg bg-avalon-bg-dark border border-avalon-purple/30 text-white placeholder-avalon-text focus:outline-none focus:border-avalon-gold transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-avalon-text hover:text-white transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            <p className="text-xs text-avalon-text mt-1.5">
              {roomPassword.length}/6
            </p>
          </div>

          {/* 房间类型 - 缩小版按钮 */}
          <div>
            <div className="flex gap-2">
              <button
                onClick={() => handleTogglePrivate(false)}
                className={`flex-1 py-2 rounded-lg border transition-all flex items-center justify-center gap-1.5 text-xs ${
                  !isPrivate
                    ? 'bg-avalon-gold text-avalon-bg-dark border-avalon-gold'
                    : 'bg-avalon-bg-dark text-avalon-text border-avalon-purple/30 hover:border-avalon-purple/50'
                }`}
              >
                <Unlock className="w-3.5 h-3.5" />
                <span>公开房间</span>
              </button>
              <button
                onClick={() => handleTogglePrivate(true)}
                className={`flex-1 py-2 rounded-lg border transition-all flex items-center justify-center gap-1.5 text-xs ${
                  isPrivate
                    ? 'bg-avalon-gold text-avalon-bg-dark border-avalon-gold'
                    : 'bg-avalon-bg-dark text-avalon-text border-avalon-purple/30 hover:border-avalon-purple/50'
                }`}
              >
                <Lock className="w-3.5 h-3.5" />
                <span>私密房间</span>
              </button>
            </div>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* 操作按钮 */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleClose}
              className="flex-1 py-3 rounded-lg bg-avalon-bg-dark text-white hover:bg-avalon-purple/50 transition-colors"
            >
              取消
            </button>
            <button
              onClick={handleCreate}
              disabled={loading}
              className={`flex-1 py-3 rounded-lg bg-avalon-gold text-avalon-bg-dark font-medium transition-colors ${
                loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-yellow-400'
              }`}
            >
              {loading ? '创建中...' : '创建房间'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
